import React, { useEffect, useState } from 'react';
import md5 from 'crypto-js/md5';
import { Tooltip } from '@mui/material';
import apis from '../../utils/apis';

// Module-level cache: deduplicates in-flight and resolved profile picture fetches
const profilePicCache = new Map<string, Promise<string>>();

/** Clear one or all cached profile picture entries so the next render re-fetches. */
export function invalidateProfilePicCache(url?: string) {
  if (url) {
    profilePicCache.delete(url);
  } else {
    profilePicCache.clear();
  }
}

function fetchProfilePic(url: string): Promise<string> {
  const cached = profilePicCache.get(url);
  if (cached) return cached;

  const promise = apis
    .get(url, { responseType: 'blob' })
    .then(
      (response) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('FileReader failed'));
          reader.readAsDataURL(response.data);
        })
    )
    .catch((err) => {
      profilePicCache.delete(url); // allow retry on failure
      throw err;
    });

  profilePicCache.set(url, promise);
  return promise;
}

interface ProfileImageProps {
  profilePicture: string | null;
  email: string | null;
  initials: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  size?: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const ProfileImage: React.FC<ProfileImageProps> = ({
  profilePicture,
  email,
  initials,
  name,
  firstName,
  lastName,
  size,
  className,
  onClick
}) => {
  const [useGravatar, setUseGravatar] = useState(false);
  const [profilePicFailed, setProfilePicFailed] = useState(false);
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);

  const getGravatarSize = () => {
    if (!size) return 100;
    if (size.includes('w-32') || size.includes('h-32') || size.includes('w-40') || size.includes('h-40')) return 512;
    if (size.includes('w-24') || size.includes('h-24')) return 256;
    if (size.includes('w-16') || size.includes('h-16')) return 128;
    if (size.includes('w-8') || size.includes('h-8') || size.includes('w-10') || size.includes('h-10')) return 100;
    return 100;
  };

  const getInitialsFontSize = () => {
    if (!size) return 'text-xs';
    if (size.includes('w-32') || size.includes('h-32') || size.includes('w-40') || size.includes('h-40')) return 'text-5xl';
    if (size.includes('w-24') || size.includes('h-24')) return 'text-3xl';
    if (size.includes('w-16') || size.includes('h-16')) return 'text-lg';
    if (size.includes('w-8') || size.includes('h-8') || size.includes('w-10') || size.includes('h-10')) return 'text-sm';
    return 'text-xs';
  };

  const displayName = firstName && lastName
    ? `${firstName} ${lastName}`
    : (name || '');

  const handleGravatarError = () => {
    setUseGravatar(false);
  };

  useEffect(() => {
    // Enable gravatar loading if email is provided and no custom profile picture
    if (email && email.trim() && !profilePicture) {
      setUseGravatar(true);
    } else {
      setUseGravatar(false);
    }
  }, [email, profilePicture]);

  // Fetch profile picture through cached helper (deduplicates across all ProfileImage instances)
  useEffect(() => {
    let cancelled = false;

    setProfilePicFailed(false);
    setResolvedSrc(null);

    if (profilePicture && profilePicture.startsWith('/user-profile/')) {
      fetchProfilePic(profilePicture)
        .then((dataUrl) => {
          if (!cancelled) setResolvedSrc(dataUrl);
        })
        .catch(() => {
          if (!cancelled) setProfilePicFailed(true);
        });
    } else if (profilePicture) {
      // External URL (e.g. already a full URL) — use directly
      setResolvedSrc(profilePicture);
    }

    return () => {
      cancelled = true;
    };
  }, [profilePicture]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (onClick) {
      onClick(e);
    }
  };

  if (resolvedSrc && !profilePicFailed) {
    return (
      <Tooltip title={displayName || ''}>
        <img
          src={resolvedSrc}
          alt="Profile"
          className={`${size ? size : 'h-7 w-7'} rounded-full object-cover flex-shrink-0 ${className}`}
          style={{ imageRendering: 'crisp-edges' }}
          loading="lazy"
          onClick={handleClick}
          onError={() => setProfilePicFailed(true)}
        />
      </Tooltip>
    );
  }

  if (useGravatar && email && email.trim()) {
    const gravatarSize = getGravatarSize();
    const emailHash = md5(email.toLowerCase().trim()).toString();
    const gravatarUrl = `https://www.gravatar.com/avatar/${emailHash}?s=${gravatarSize}&d=mp&r=g`;
    return (
      <Tooltip title={displayName || ''}>
        <img
          src={gravatarUrl}
          alt={displayName || 'User avatar'}
          className={`${size ? size : 'h-7 w-7'} rounded-full object-cover flex-shrink-0 ${className}`}
          style={{ imageRendering: 'crisp-edges' }}
          loading="lazy"
          onClick={handleClick}
          onError={handleGravatarError}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={displayName || ''}>
      <div
        className={`${size ? size : 'h-7 w-7'} rounded-full bg-primary-600 dark:bg-primary-500 flex items-center justify-center flex-shrink-0 cursor-pointer hover:shadow-lg transition-shadow duration-200 ${className}`}
        style={{ imageRendering: 'auto' }}
        onClick={handleClick}
      >
        <span className={`${getInitialsFontSize()} text-white select-none`}>
          {initials}
        </span>
      </div>
    </Tooltip>
  );
};

export default ProfileImage;
